import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { change, reduxForm, InjectedFormProps } from "redux-form";
import { useEffect } from "react";
import Button from "components/atoms/Button";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { modalActions } from "redux/slices/modal";
import NewPricingService from "services/newPricing.service";
import { commissionActions } from "redux/slices/commission";
import { mapAction } from "redux/slices/map/mapSlice";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";

const ACTION_CONTROL_HEIGHT = 40;

type OwnProps = {
  isGeocoding?: boolean;
};

type EditMapInputFormProps = OwnProps & InjectedFormProps<{}, OwnProps, string>;

function EditMapInputForm({ isGeocoding = false }: EditMapInputFormProps) {
  const bookingForm = "AddBookingForm";
  const form = "EditMapInputForm";
  const dispatch = useAppDispatch();
  const show = useAppSelector((state) => state.map.show);
  const pickup = useAppSelector(
    (state) => state.form?.[bookingForm]?.values?.pickup?.value
  );
  const destination = useAppSelector(
    (state) => state.form?.[bookingForm]?.values?.destination?.value
  );
  const company_id = useAppSelector(
    (state) => state.form?.[bookingForm]?.values?.company
  );
  const companies = useAppSelector((state) => state.company.companies);
  const startAddress = useAppSelector((state) => state.map?.startMapformatted);
  const endAddress = useAppSelector((state) => state.map?.endMapformatted);

  const isPickup = show === 0;
  const activeAddress = isPickup ? startAddress : endAddress;
  const fallbackAddress = isPickup ? pickup : destination;
  const displayAddress = activeAddress?.value || fallbackAddress || "";
  const hasAddress = Boolean(activeAddress?.value);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        if (company_id) {
          const data = { company_id: company_id.value };
          const company = companies.find(
            (item: any) => item._id === company_id.value
          );
          if (company?.isFixedPrice) {
            await NewPricingService.getAllCompanyFixedNewPricings(data);
          } else {
            await NewPricingService.getAllNewPricings("?type=corporate");
          }
        } else {
          await NewPricingService.getAllNewPricings("?type=individual");
        }
      } catch (err) {
        console.error("Failed to fetch pricing", err);
      }
    };
    fetchPricing();
    return () => {
      dispatch(commissionActions.setCommission(null));
    };
  }, [company_id, companies, dispatch]);

  useEffect(() => {
    const startAdd = startAddress?.value ? startAddress?.value : pickup;
    const endAdd = endAddress?.value ? endAddress?.value : destination;
    if (show === 0) dispatch(change(form, "pickup", startAdd));
    if (show === 1) dispatch(change(form, "destination", endAdd));
  }, [startAddress, endAddress, dispatch, destination, pickup, form, show]);

  const handleMapData = () => {
    if (show === 0 && startAddress) {
      dispatch(mapAction.setStartFormatted(startAddress));
    }
    if (show === 1 && endAddress) {
      dispatch(mapAction.setEndFormatted(endAddress));
    }
  };

  const handleClose = () => {
    dispatch(mapAction.setStartMapFormatted(null));
    dispatch(mapAction.setEndMapFormatted(null));
    dispatch(modalActions.closeModal());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleMapData();
    dispatch(modalActions.closeModal());
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box
        sx={{
          pb: 2,
          mb: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(82, 10, 118, 0.1)",
              color: "primary.main",
            }}
          >
            <LocationOnOutlinedIcon fontSize="small" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              lineHeight={1.3}
              sx={{ mb: 0.25 }}
            >
              Set {isPickup ? "pickup" : "destination"} location
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
              }}
            >
              <NearMeOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" lineHeight={1.4}>
                Drag the map to fine-tune the exact point
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close map picker"
            sx={{
              flexShrink: 0,
              mt: -0.5,
              color: "text.secondary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography
          component="label"
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mb: 0.75,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          Selected address
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
            gap: { xs: 1.5, sm: 1.5 },
            height: { xs: "auto", sm: ACTION_CONTROL_HEIGHT },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              borderRadius: 1,
              bgcolor: "#fafafa",
              height: { xs: ACTION_CONTROL_HEIGHT, sm: "100%" },
              boxSizing: "border-box",
              transition: "background-color 0.2s ease",
              ...(hasAddress && {
                bgcolor: "#f7f7f7",
              }),
            }}
          >
            <LocationOnOutlinedIcon
              sx={{
                color: "primary.main",
                flexShrink: 0,
                fontSize: 20,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {isGeocoding ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={14} color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Updating address…
                  </Typography>
                </Box>
              ) : displayAddress ? (
                <Tooltip title={displayAddress} placement="top-start">
                  <Typography variant="body2" noWrap fontWeight={500}>
                    {displayAddress}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Move the map to select a location
                </Typography>
              )}
            </Box>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            startIcon={<CheckIcon sx={{ fontSize: 18 }} />}
            disabled={!hasAddress || isGeocoding}
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
              whiteSpace: "nowrap",
              height: { xs: ACTION_CONTROL_HEIGHT, sm: "100%" },
              minHeight: ACTION_CONTROL_HEIGHT,
              maxHeight: { sm: ACTION_CONTROL_HEIGHT },
              boxSizing: "border-box",
              py: 0,
              px: 2.5,
              lineHeight: 1,
              borderRadius: 1,
              alignSelf: { sm: "stretch" },
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
              "&.MuiButton-root": {
                minHeight: "unset",
              },
              "& .MuiButton-startIcon": {
                marginRight: 0.5,
                marginLeft: 0,
              },
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </form>
  );
}

export default reduxForm<{}, OwnProps>({ form: "EditMapInputForm" })(
  EditMapInputForm
);
